import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PropTypes from "prop-types";
import { format } from "date-fns";
import moment from "moment";
import PeoplePicker from "components/Common/PeoplePicker/PeoplePicker";

const CustomBulkEditHeader = ({
  field,
  type,
  headerTitle,
  headerTooltip,
  onBulkEdit,
  selectedRows,
  setSnack,
  resetValue,
  valueOptions,
}) => {
  const [value, setValue] = useState(type === "boolean" ? false : "");
  const [clearPicker, setClearPicker] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const restrictedFields = [
    "rosterLockBusDays",
    "ttecAccessSLABusDays",
    "clientAccessSLABusDays",
  ];

  useEffect(() => {
    let initialValue;
    if (type === "boolean") {
      initialValue = false;
    } else if (type === "date") {
      initialValue = null;
    } else if (type === "peoplepicker") {
      initialValue = null;
      setClearPicker((prev) => prev + 1);
    } else {
      initialValue = "";
    }
    setValue(initialValue);
    setAnchorEl(null);
  }, [resetValue, type]);

  const handleBulkEdit = (event) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    let newValue =
      type === "boolean" ? event.target.checked : event.target.value;

    if (type === "number") {
      const numericValue = Number(newValue);
      if (isNaN(numericValue) || numericValue < 0) {
        setSnack({
          open: true,
          message: "Negative values are not allowed.",
          severity: "error",
        });
        return;
      }
      newValue = numericValue;
    }

    setValue(newValue);
    onBulkEdit(field, newValue);
  };

  const handleDateChange = (newValue) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    const formattedDate = newValue
      ? format(new Date(newValue), "MM/dd/yyyy")
      : "";
    setValue(formattedDate);
    onBulkEdit(field, formattedDate);
  };

  const handlePeoplePickerChange = (employee) => {
    if (employee) {
      setValue(employee);
      onBulkEdit(field, employee);
      handleClosePopover();
    }
  };

  const handlePeoplePickerReset = () => {
    setValue(null);
    setClearPicker((prev) => prev + 1);
  };

  const handleOpenPopover = (event) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "Please select at least one row to use bulk edit.",
        severity: "warning",
      });
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleBlur = (event) => {
    if (
      event.relatedTarget &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    event.stopPropagation();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const isShift = event.shiftKey;
      const focusableElements = document.querySelectorAll('[tabindex="0"]');
      const currentIndex = Array.from(focusableElements).indexOf(event.target);

      let nextIndex = isShift ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = focusableElements.length - 1;
      if (nextIndex >= focusableElements.length) nextIndex = 0;

      focusableElements[nextIndex]?.focus();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      sx={{ height: "100%", justifyContent: "center" }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Tooltip title={headerTooltip || headerTitle} placement="top">
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.10em",
            fontWeight: "bold",
          }}
        >
          {headerTitle}
        </Typography>
      </Tooltip>

      {type === "peoplepicker" && (
        <>
          <IconButton size="small" onClick={handleOpenPopover} sx={{ mt: 0.5 }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Box sx={{ p: 2, width: 350 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Bulk Edit {headerTitle}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 2, color: "text.secondary" }}
              >
                This will update all {selectedRows.length} selected row(s)
              </Typography>
              <PeoplePicker
                label=""
                placeholder="Search employee"
                onChange={handlePeoplePickerChange}
                onReset={handlePeoplePickerReset}
                clearFields={clearPicker}
              />
            </Box>
          </Popover>
        </>
      )}

      {type === "singleSelect" && field === "idmSupport" && (
        <Select
          fullWidth
          size="small"
          value={value}
          onChange={handleBulkEdit}
          onBlur={handleBlur}
          displayEmpty
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="Out of Scope">Out of Scope</MenuItem>
          <MenuItem value="Full Support">Full Support</MenuItem>
          <MenuItem value="Other TTEC Access">Other TTEC Access</MenuItem>
          <MenuItem value="Phone IDs Only">Phone IDs Only</MenuItem>
        </Select>
      )}

      {type === "singleSelect" && field === "idmDeploymentStatus" && (
        <Select
          fullWidth
          size="small"
          value={value}
          onChange={handleBulkEdit}
          onBlur={handleBlur}
          data-reset="true"
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="Complete">Complete</MenuItem>
          <MenuItem value="Ramp Down">Ramp Down</MenuItem>
          <MenuItem value="New Launch">New Launch</MenuItem>
          <MenuItem value="Out of Scope">Out of Scope</MenuItem>
        </Select>
      )}

      {type === "singleSelect" &&
        field !== "idmSupport" &&
        field !== "idmDeploymentStatus" &&
        Array.isArray(valueOptions) &&
        valueOptions.length > 0 && (
          <Select
            fullWidth
            size="small"
            value={value}
            onChange={handleBulkEdit}
            onBlur={handleBlur}
            displayEmpty
          >
            <MenuItem value="">Select</MenuItem>
            {valueOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        )}

      {type === "boolean" && (
        <Checkbox checked={value} onChange={handleBulkEdit} />
      )}
      {type === "string" && (
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleBulkEdit}
        />
      )}
      {type === "number" && (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={value}
          onChange={(event) => {
            let input = event.target.value;
            if (input === "") {
              setValue("");
              return;
            }
            const sanitized = input.replace(/^0+(?!$)/, "");
            let numericValue = Number(sanitized);
            if (restrictedFields.includes(field)) {
              if (
                !isNaN(numericValue) &&
                numericValue >= 0 &&
                numericValue <= 99
              ) {
                setValue(numericValue);
                onBulkEdit(field, numericValue);
              }
            } else {
              if (!isNaN(numericValue) && numericValue >= 0) {
                setValue(numericValue);
                onBulkEdit(field, numericValue);
              } else {
                setSnack({
                  open: true,
                  message: "Negative values are not allowed.",
                  severity: "error",
                });
              }
            }
          }}
          onBlur={(event) => {
            let input = event.target.value;
            if (input !== "") {
              const sanitized = input.replace(/^0+(?!$)/, "");
              let numericValue = Number(sanitized);
              if (restrictedFields.includes(field)) {
                if (numericValue < 0) numericValue = 0;
                if (numericValue > 99) numericValue = 99;
              }
              setValue(numericValue);
              onBulkEdit(field, numericValue);
            }
          }}
          inputProps={{
            min: 0,
            max: restrictedFields.includes(field) ? 99 : undefined,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />
      )}
      {type === "date" && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            type="date"
            inputFormat="MM/DD/YYYY"
            renderInput={(props) => (
              <TextField {...props} fullWidth size="small" error={false} />
            )}
            value={moment(value)}
            onChange={handleDateChange}
          />
        </LocalizationProvider>
      )}
    </Box>
  );
};

CustomBulkEditHeader.propTypes = {
  field: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    "singleSelect",
    "boolean",
    "string",
    "number",
    "date",
    "peoplepicker",
  ]).isRequired,
  headerTitle: PropTypes.string.isRequired,
  headerTooltip: PropTypes.string,
  onBulkEdit: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  setSnack: PropTypes.func.isRequired,
  resetValue: PropTypes.bool.isRequired,
  valueOptions: PropTypes.arrayOf(PropTypes.string), // optional — only needed for dynamic singleSelect
};
export default CustomBulkEditHeader;
